import { supabase } from "./supabase";
import { nanoid } from "nanoid";

function getErrorMessage(error) {
  if (error instanceof Error) return error.message;
  return String(error);
}

// ============================================
// Contact Functions
// ============================================

export async function findContactByEmail(email) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data, error } = await supabase
      .from("contacts")
      .select("*")
      .eq("email", email)
      .single();

    if (error && error.code !== "PGRST116") {
      return { success: false, error: error.message };
    }

    return { success: true, data: data || null };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function createContact(contactData) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data, error } = await supabase
      .from("contacts")
      .insert([
        {
          firstname: contactData.firstname,
          lastname: contactData.lastname || null,
          email: contactData.email,
          company: contactData.company || null,
          industry: contactData.industry || null,
          numemployees: contactData.numemployees || null,
        },
      ])
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, code: error.code };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function updateContact(contactId, updates) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data, error } = await supabase
      .from("contacts")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", contactId)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

// ============================================
// Session Functions
// ============================================

export async function generateUniqueSessionCode() {
  if (!supabase) {
    return nanoid(8).toUpperCase();
  }

  let attempts = 0;
  const maxAttempts = 5;

  while (attempts < maxAttempts) {
    const code = nanoid(8).toUpperCase();

    const { data: existingSession } = await supabase
      .from("sessions")
      .select("id")
      .eq("id", code)
      .single();

    if (!existingSession) {
      return code;
    }

    attempts++;
  }

  throw new Error("Failed to generate unique session code after maximum attempts");
}

export async function createSession(contactId) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const sessionId = await generateUniqueSessionCode();
    const now = new Date();
    const expirationDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const { data, error } = await supabase
      .from("sessions")
      .insert([
        {
          id: sessionId,
          contact_id: contactId,
          current_page: 1,
          status: "active",
          last_active_at: now.toISOString(),
          expiration_date: expirationDate.toISOString(),
          responses: {},
        },
      ])
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, code: "CREATE_FAILED" };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error), code: "CREATE_FAILED" };
  }
}

export async function getSessionWithContact(sessionCode) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data: session, error: sessionError } = await supabase
      .from("sessions")
      .select("*")
      .eq("id", sessionCode)
      .single();

    if (sessionError) {
      return {
        success: false,
        error: sessionError.code === "PGRST116" ? "Session not found" : sessionError.message,
      };
    }

    if (!session) {
      return { success: false, error: "Session not found" };
    }

    const isExpired = new Date(session.expiration_date) < new Date();
    if (session.status === "active" && isExpired) {
      await updateSessionProgress(sessionCode, { status: "expired" });
      return { success: false, error: "Session has expired" };
    }

    const { data: contact, error: contactError } = await supabase
      .from("contacts")
      .select("*")
      .eq("id", session.contact_id)
      .single();

    if (contactError || !contact) {
      return { success: false, error: "Contact not found" };
    }

    const combinedData = {
      ...session,
      contact,
      responses: session.responses || {},
    };

    return { success: true, data: combinedData };
  } catch (error) {
    return { success: false, error: getErrorMessage(error), code: "FETCH_FAILED" };
  }
}

export async function updateSessionProgress(sessionCode, updates) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data, error } = await supabase
      .from("sessions")
      .update({
        ...updates,
        last_active_at: new Date().toISOString(),
      })
      .eq("id", sessionCode)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function updateSessionLastActive(sessionCode) {
  return updateSessionProgress(sessionCode, {});
}

export async function updateSessionScores(sessionCode, q4Score, q7Score) {
  return updateSessionProgress(sessionCode, {
    q4_score: q4Score,
    q7_score: q7Score,
  });
}

export async function markSessionCompleted(sessionCode) {
  return updateSessionProgress(sessionCode, {
    status: "completed",
  });
}

export async function checkInsightsReportEligibility(email) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return {
      eligible: false,
      error: "Supabase not configured"
    };
  }

  try {
    const { data: contact, error } = await supabase
      .from("contacts")
      .select("last_insights_report_requested_at")
      .eq("email", email)
      .single();

    if (error) {
      return {
        eligible: false,
        error: error.message
      };
    }

    if (!contact || !contact.last_insights_report_requested_at) {
      return {
        eligible: true,
        lastRequestDate: null,
        daysUntilEligible: 0
      };
    }

    const lastRequestDate = new Date(contact.last_insights_report_requested_at);
    const now = new Date();
    const daysSinceRequest = Math.floor((now - lastRequestDate) / (1000 * 60 * 60 * 24));
    const daysUntilEligible = Math.max(0, 365 - daysSinceRequest);

    return {
      eligible: daysSinceRequest >= 365,
      lastRequestDate,
      daysUntilEligible,
    };
  } catch (error) {
    return {
      eligible: false,
      error: getErrorMessage(error)
    };
  }
}

async function updateContactLastInsightsRequest(contactId) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { error } = await supabase
      .from("contacts")
      .update({
        last_insights_report_requested_at: new Date().toISOString(),
      })
      .eq("id", contactId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function updateRequestFullInsightsReport(sessionCode, contactEmail) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { success: false, error: "Supabase not configured" };
  }

  try {
    const { data: session, error: fetchError } = await supabase
      .from("sessions")
      .select("request_full_insights_report, contact_id")
      .eq("id", sessionCode)
      .single();

    if (fetchError) {
      return { success: false, error: fetchError.message };
    }

    if (session?.request_full_insights_report === true) {
      const { data: fullSession } = await supabase
        .from("sessions")
        .select("*")
        .eq("id", sessionCode)
        .single();

      return { success: true, data: fullSession, alreadySet: true };
    }

    if (contactEmail) {
      const eligibility = await checkInsightsReportEligibility(contactEmail);

      if (!eligibility.eligible) {
        return {
          success: false,
          yearLimitReached: true,
          lastRequestDate: eligibility.lastRequestDate,
          daysUntilEligible: eligibility.daysUntilEligible,
          error: eligibility.error,
        };
      }
    }

    const { data, error } = await supabase
      .from("sessions")
      .update({
        request_full_insights_report: true,
        last_active_at: new Date().toISOString(),
      })
      .eq("id", sessionCode)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    if (session?.contact_id) {
      await updateContactLastInsightsRequest(session.contact_id);
    }

    return { success: true, data, alreadySet: false };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
}

export async function checkActiveSession(email) {
  if (!supabase) {
    console.warn("Supabase not configured");
    return { hasActiveSession: false, session: null };
  }

  try {
    const { data: contact, error: contactError } = await supabase
      .from("contacts")
      .select("id")
      .eq("email", email)
      .single();

    if (contactError || !contact) {
      return { hasActiveSession: false, session: null };
    }

    const { data, error } = await supabase
      .from("sessions")
      .select("*")
      .eq("contact_id", contact.id)
      .eq("status", "active")
      .gt("expiration_date", new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) {
      console.error("[Supabase] Active session check error:", error);
      return { hasActiveSession: false, session: null };
    }

    return {
      hasActiveSession: data && data.length > 0,
      session: data?.[0] || null,
    };
  } catch (error) {
    console.error("[Supabase] Active session check error:", getErrorMessage(error));
    return { hasActiveSession: false, session: null };
  }
}

export async function getOrCreateContact(contactData) {
  const existingContact = await findContactByEmail(contactData.email);

  if (existingContact.success && existingContact.data) {
    return { success: true, data: existingContact.data, isNew: false };
  }

  const newContact = await createContact(contactData);
  if (newContact.success) {
    return { success: true, data: newContact.data, isNew: true };
  }

  if (newContact.code === "23505") {
    const retryContact = await findContactByEmail(contactData.email);
    if (retryContact.success && retryContact.data) {
      return { success: true, data: retryContact.data, isNew: false };
    }
  }

  return newContact;
}

export async function initializeSession(contactData) {
  const contactResult = await getOrCreateContact(contactData);

  if (!contactResult.success) {
    return { success: false, error: contactResult.error };
  }

  const activeSessionCheck = await checkActiveSession(contactData.email);

  if (activeSessionCheck.hasActiveSession && activeSessionCheck.session) {
    return {
      success: true,
      sessionCode: activeSessionCheck.session.id,
      contactId: contactResult.data.id,
      isExisting: true,
      session: activeSessionCheck.session,
    };
  }

  const sessionResult = await createSession(contactResult.data.id);

  if (!sessionResult.success) {
    return { success: false, error: sessionResult.error };
  }

  return {
    success: true,
    sessionCode: sessionResult.data.id,
    contactId: contactResult.data.id,
    isExisting: false,
    session: sessionResult.data,
  };
}
